import { useEffect, useMemo, useRef } from 'react';
import type { StepSchema, RunState } from 'shared';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import { audioEngine } from '../../audio/audioEngine';

type Props = {
  step: StepSchema | null;
  state?: RunState | null;
};

type DialogCursor = { stepId: string; index: number };
const DIALOG_CURSOR_FLAG_KEY = '__dialogCursor';
function readDialogCursor(flags: Record<string, unknown> | undefined | null): DialogCursor | null {
  const raw = (flags as any)?.[DIALOG_CURSOR_FLAG_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as any;
  if (typeof r.stepId !== 'string' || !Number.isFinite(r.index)) return null;
  return { stepId: r.stepId, index: Number(r.index) };
}

/**
 * P8-1: BGM 指示の3値。
 * - play: この URL を鳴らす（同一 URL は engine 側で継続扱い）
 * - stop: 明示停止（dialog の bgm.stop=true のみ）
 * - keep: 直前の BGM を継続する（既定）
 *
 * 従来は「scene/dialog/minigame 以外の type」「bgm 未指定」を全て null=停止に倒していたため、
 * spot_gate/branch/変数変更等に入るたび 600ms フェードで強制無音化していた（P8 監査の因子2）。
 * 現在の契約: **BGM は次の指定（新しい src か stop:true）まで継続する**。
 * 明示的に止める手段は dialog（step/line）の bgm.stop=true（既存スキーマ・追加なし）。
 */
type BgmDirective =
  | { kind: 'play'; url: string }
  | { kind: 'stop' }
  | { kind: 'keep' };

const KEEP: BgmDirective = { kind: 'keep' };

function resolveBgmDirective(step: StepSchema | null, state?: RunState | null): BgmDirective {
  if (!step) return KEEP;
  if (step.type === 'scene') {
    const url = resolveMediaRefToUrl(step.bgm?.src);
    return url ? { kind: 'play', url } : KEEP;
  }
  if (step.type === 'minigame') {
    const cfg = step.config as any;
    // パターン1: BaseConfigSchema (templates 1-9) - audio.bgm.assetId or audio.bgm.url
    const audioRef = cfg?.audio?.bgm?.assetId ?? cfg?.audio?.bgm?.url;
    if (typeof audioRef === 'string' && audioRef.trim()) {
      return { kind: 'play', url: resolveMediaRefToUrl(audioRef.trim()) ?? audioRef.trim() };
    }
    // パターン2: AssetIdRefSchema (templates 10-14) - assets.bgmAssetId
    const assetsRef = cfg?.assets?.bgmAssetId;
    if (typeof assetsRef === 'string' && assetsRef.trim()) {
      return { kind: 'play', url: resolveMediaRefToUrl(assetsRef.trim()) ?? assetsRef.trim() };
    }
    return KEEP;
  }
  if (step.type === 'dialog') {
    const cursor = state ? readDialogCursor(state.flags) : null;
    const lines = Array.isArray((step as any).lines) ? ((step as any).lines as any[]) : null;
    const idx = lines && lines.length > 0 ? (cursor && cursor.stepId === step.id ? cursor.index : 0) : 0;
    // 区間差し替え: 0..idx を走査して最後に指定されたBGM設定を採用（resetで戻す）
    let active = (step as any).bgm as any | null;
    if (lines && lines.length > 0) {
      for (let i = 0; i <= Math.min(idx, lines.length - 1); i += 1) {
        const line = lines[i];
        if (line?.resetBgm) {
          active = (step as any).bgm as any | null;
        }
        if (line?.bgm) {
          active = line.bgm;
        }
      }
    }
    if (active?.stop) {
      // スキーマの意図どおり「src 無しでも停止指示できる」唯一の明示停止。
      return { kind: 'stop' };
    }
    const src = typeof active?.src === 'string' ? active.src : null;
    if (src) {
      return { kind: 'play', url: resolveMediaRefToUrl(src) ?? src };
    }
    return KEEP;
  }
  if (step.type === 'ending') {
    // ending は演出用の bgmAssetId（既存スキーマ・従来は誰も鳴らしていなかった）を尊重する。
    const src = (step as any).bgmAssetId;
    if (typeof src === 'string' && src.trim()) {
      return { kind: 'play', url: resolveMediaRefToUrl(src.trim()) ?? src.trim() };
    }
    return KEEP;
  }
  // spot_gate / branch / free_input / line_dialog / reward / variable_mutation / ai_dialog:
  // bgm フィールドを持たない type では直前の BGM を継続する（強制無音化の解消）。
  return KEEP;
}

/**
 * BGM コントローラ（R2 感覚層・Wave2A でグローバル音エンジンへ委譲）。
 *
 * step / state から「いま鳴らすべき BGM の指示（play/stop/keep）」を解決し、
 * play は audioEngine.playBgm() / stop は stopBgm() に渡し、keep では何もしない薄い層。
 * 独自 HTMLAudio 再生や enable トグルは廃止し、再生・音量・ミュートは全て master gain 経由で
 * 一元管理される（StartScreen やミニゲームと同じ統括下）。
 *
 * autoplay policy 上、unlock 前は playBgm は src だけ保持して鳴らない（AudioProvider が初回
 * ジェスチャで unlock すると鳴り始める。P8-1 で unlock 側に再試行を追加済み）。
 * BGM アセットの無いシナリオでは一度も play が発行されないので無音のまま。
 *
 * なお `<audio data-testid="bgm-audio">` は「解決済み URL の観測点」としてのみ残置する
 * （Playwright が src を読む既存テスト互換）。muted かつ preload=none・.play() は決して呼ばないため、
 * この要素からは一切音は出ない（実音は engine 側の HTMLAudio が担う）。
 * keep の間は直前の src を保持し続ける（「いま鳴っているはずの BGM」を観測できる）。
 */
export function BgmController({ step, state }: Props) {
  // テスト観測用の inert な audio 要素（音は出さない・src のみ反映）
  const mirrorRef = useRef<HTMLAudioElement | null>(null);
  const directive = useMemo(() => resolveBgmDirective(step, state), [step, state]);
  const playUrl = directive.kind === 'play' ? directive.url : null;

  useEffect(() => {
    // keep は「何もしない」＝直前の再生（または無音）を継続する。
    if (directive.kind === 'keep') return;

    // 観測用ミラーの src を解決済み URL に合わせる（再生はしない）
    const mirror = mirrorRef.current;
    if (mirror) {
      if (directive.kind === 'play') {
        mirror.src = directive.url;
      } else {
        mirror.removeAttribute('src');
      }
    }

    // 実音はグローバル音エンジンへ委譲。play なら再生（同一 URL は engine 側で継続扱い）、
    // stop（dialog の bgm.stop=true）なら停止。これで master mute / volume に自動連動する。
    if (directive.kind === 'play') {
      audioEngine.playBgm(directive.url, { loop: true });
    } else {
      audioEngine.stopBgm();
    }
    // playUrl を依存に取り「同じ URL の play が連続する再レンダー」で再発火させない
    // （旧実装の [url] 依存と同じ保護。kind だけ変わる keep→play 等は kind 側で拾う）。
  }, [directive.kind, playUrl]);

  // アンマウント時は BGM を止める（プレイ画面離脱・StartScreen 復帰時の鳴りっぱなし防止）
  useEffect(() => {
    return () => {
      audioEngine.stopBgm();
    };
  }, []);

  return (
    // Playwright 等で解決済み URL を参照するために src を持つ inert な audio を常設する。
    // muted + preload=none + 非 .play() のため、この要素からは音は出ない。
    <audio ref={mirrorRef} data-testid="bgm-audio" muted preload="none" />
  );
}
